import * as Sentry from '@sentry/vue'
import axios from 'axios'
import Vue from 'vue'

import { i18n } from '@/i18n'
import { getAdTokenRedirect } from '@/utils/active_directory_helper'

axios.defaults.withCredentials = true

const baseAuthHeader = async () => {
  const token = await getAdTokenRedirect()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export const baseUrl = process.env.VUE_APP_BASE_API

export const get = async ({ path, params, options }) => {
  try {
    const response = await axios.get(`${baseUrl}/${path}`, {
      headers: await baseAuthHeader(),
      ...(!!params && { params: { ...params } }),
    })
    return { success: true, data: response.data }
  } catch (error) {
    const errors = error.response?.data.errors
    const status = error.response?.status
    handleAPIErrors(errors, options?.skipErrorNotify)
    return { success: false, errors, status }
  }
}

export const patch = async ({ path, params, options }) => {
  try {
    const response = await axios.patch(`${baseUrl}/${path}`, params, {
      headers: await baseAuthHeader(),
    })
    if (!options?.skipNotify) {
      Vue.notify({
        group: 'alert',
        type: 'success',
        title: options?.message || i18n.t('meta.successfullyUpdated'),
      })
    }
    return { success: true, data: response.data }
  } catch (error) {
    const errors = error.response?.data.errors
    handleAPIErrors(errors)
    return { success: false, errors: errors }
  }
}

export const post = async ({ path, params, options }) => {
  try {
    const response = await axios.post(`${baseUrl}/${path}`, params, {
      headers: await baseAuthHeader(),
    })
    if (!options?.skipNotify) {
      Vue.notify({
        group: 'alert',
        type: 'success',
        title: options?.notificationMessage || i18n.t('meta.successfullySaved'),
      })
    }
    return { success: true, data: response.data }
  } catch (error) {
    const errors = error.response?.data.errors
    handleAPIErrors(errors)
    return { success: false, errors: errors }
  }
}

export const remove = async ({ path, params, options }) => {
  try {
    const response = await axios.delete(`${baseUrl}/${path}`, {
      headers: await baseAuthHeader(),
      ...(!!params && { params: { ...params } }),
    })

    if (!options?.skipNotify) {
      Vue.notify({
        group: 'alert',
        type: 'success',
        title: options?.message || i18n.t('meta.successfullyDeleted'),
      })
    }
    return { success: true, data: response.data }
  } catch (error) {
    const errors = error.response?.data.errors
    handleAPIErrors(errors)
    return { success: false, errors: errors }
  }
}

export const downloadStream = async ({ path, params, responseType, mimeType, defaultExtension }) => {
  try {
    const response = await axios.get(`${baseUrl}/${path}`, {
      responseType: responseType,
      headers: await baseAuthHeader(),
      ...(!!params && { params: { ...params } }),
    })
    const streamContents = new Blob([response.data], { type: mimeType })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(streamContents)
    const filenameMatches = response.headers['content-disposition'].match('filename="([^"]+)"')
    if (filenameMatches) {
      // Use server sent filename
      link.download = filenameMatches[1]
    } else {
      link.download = `export.${defaultExtension}`
    }
    link.click()
    URL.revokeObjectURL(link.href)
  } catch (error) {
    // errors are still rendered in JSON
    const errors = error.response?.data.errors
    handleAPIErrors(errors)
  }
}

export const downloadCsv = async ({ path, params }) => {
  return downloadStream({
    path,
    params,
    responseType: 'stream',
    mimeType: 'text/csv',
    defaultExtension: 'csv',
  })
}

export const downloadExcel = async ({ path, params }) => {
  return downloadStream({
    path,
    params,
    responseType: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
    defaultExtension: 'xlsx',
  })
}

export const getPaginated = async ({ path, params, valuePath, maxPages }) => {
  const values = []
  let page = 1
  const urlDelimiter = path.includes('?') ? '&' : '?'

  while (page != null) {
    const response = await get({
      path: `${path}${urlDelimiter}page=${page}`,
      params,
    })

    if (response.success === false) return { success: false, error: response } // propagates the error

    values.push(...response.data[valuePath])

    if (response.data.meta.pagination.links.next != undefined) {
      let nextParamsString = response.data.meta.pagination.links.next.split('?')[1]
      let nextParams = new URLSearchParams(nextParamsString)
      page = nextParams.get('page')
    } else {
      page = null
    }

    if (maxPages != null && maxPages < page) break
  }

  return {
    success: true,
    values: values,
  }
}

export const getWithPagination = async ({ path, params, valuePath }) => {
  const values = []
  const urlDelimiter = path.includes('?') ? '&' : '?'
  let totalPages = null

  for (let page = 1; totalPages == null || page <= totalPages; page++) {
    const response = await get({
      path: `${path}${urlDelimiter}page=${page}`,
      params,
    })
    if (response.success === false) return { success: false, error: response }
    totalPages = response.data.meta.pagination.total_pages
    const dataValues = response.data[valuePath]
    values.push(...dataValues)
  }

  return values
}

function handleAPIErrors(errors, skipNotify) {
  if (errors?.length > 0) {
    for (const value of errors) {
      Sentry.captureException(value)
      if (!skipNotify) {
        Vue.notify({
          group: 'alert',
          type: 'error',
          text: value,
        })
      }
    }
  }
}
